import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, cn } from '@playerbase/ui';
import type { DiscordGuildChannel } from '@playerbase/types';

import { toBuildChanges } from '../games/build-timeline.js';
import { useShell } from '../shell/use-shell.js';
import { ActivityPanel } from './activity-panel.js';
import { BuildChangedRow } from './build-changed-row.js';
import { FeedbackChannelFilter, type ChannelChip } from './feedback-channel-filter.js';
import { FeedbackEmptyState } from './feedback-empty-state.js';
import { FeedbackGroup } from './feedback-group.js';
import { FormSubmissionsFeed } from './form-submissions-feed.js';
import { interleaveBuildMarkers } from './interleave-build-markers.js';
import { PollsFeed, isClosed } from './polls-feed.js';
import {
  useFeedbackInbox,
  type ActivityEntry,
  type InboxEntityFilter,
  type InboxMessage,
} from './use-feedback-inbox.js';
import { useWatchedEntities } from './use-watched-entities.js';

// ---------------------------------------------------------------------------
// Feedback inbox container.
//
// Composes the data hook + filter chip group + group list. The hook
// already drops signal_flags.isEmpty messages from `rows` and groups
// forum threads + reply chains, so the container just renders. The
// full `messages` set (including isEmpty edges) is used to build a
// Map<external_id, InboxMessage> for reply-quote lookup - an empty
// image-only message can still be a valid reply target even though
// we don't render it as its own bundle.
//
// Chips are sourced from the studio's watched-entity set
// (discord_connections.watched_channel_ids hydrated through
// discord_guild_channels), NOT from the row data. That way the picker
// only ever shows entities the studio explicitly opted into - forums,
// channels, and individually watched threads. Threads that emerged
// inside a watched parent forum still appear in the feed but don't
// add their own chip.
//
// PR 5 adds a Form submissions pane to the right column, alongside
// Polls and Messages. The right column is now a tab strip; clicking
// an activity entry routes to the right tab and pulses the matching
// row in that pane.
// ---------------------------------------------------------------------------

// Stable string id for a chip. Keeps FeedbackChannelFilter's
// string-id contract while letting the container resolve the chip
// back to the entity when the user clicks.
function chipIdFor(entity: DiscordGuildChannel): string {
  return `${entity.type}:${entity.channelId}`;
}

function chipLabelFor(entity: DiscordGuildChannel): string {
  if (entity.type === 'thread') {
    // Threads sit inside a parent forum/channel; the arrow makes that
    // visible at chip level without needing the parent name (which can
    // be very long).
    return `${entity.name} ↳`;
  }
  if (entity.type === 'forum') {
    // Forums hold threaded conversations with a different inner shape
    // (forum-post cards, not flat chat rows). Leading with ↪ makes the
    // distinction visible at chip level so a user reading the picker
    // can tell apart a #channel from a ↪forum without expanding it.
    return `↪ ${entity.name}`;
  }
  return `#${entity.name}`;
}

const TYPE_ORDER: Record<DiscordGuildChannel['type'], number> = {
  forum: 0,
  text: 1,
  announcement: 1,
  thread: 2,
};

function sortEntitiesForChips(entities: DiscordGuildChannel[]): DiscordGuildChannel[] {
  return entities
    .slice()
    .sort((a, b) => {
      const t = TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
      if (t !== 0) return t;
      return a.position - b.position;
    });
}

// Visible-cap rhythm. Each list shows 10 groups initially with a
// "Show more" button to reveal another 10. Once the local cap meets
// the fetched count on the feed side, the same button switches to a
// networked load-more (the data hook's existing pagination).
const INITIAL_VISIBLE = 10;
const VISIBLE_STEP = 10;
// How long the activity-panel click highlight stays on the matched
// row. Long enough to draw the eye after a smooth scroll, short
// enough that it doesn't linger as visual debt.
const HIGHLIGHT_DURATION_MS = 2500;

// Translate a chip selection back into the entity-filter shape the
// data hook expects. Forum chips filter by parent name (forum
// messages live inside threads that share their parent's name);
// thread/channel chips filter by the channel snowflake.
function entityFilterFor(entity: DiscordGuildChannel): InboxEntityFilter {
  if (entity.type === 'forum') {
    return { kind: 'forum', name: entity.name };
  }
  if (entity.type === 'thread') {
    return { kind: 'thread', channelId: entity.channelId };
  }
  return { kind: 'channel', channelId: entity.channelId };
}

// Timestamp an activity entry is keyed on for the unread dot + read
// watermark. Groups bump on their latest activity; submissions and build
// changes anchor on their own creation instant.
function activityUnreadTimestamp(entry: ActivityEntry): string {
  if (entry.kind === 'group') return entry.group.lastActivityAt;
  if (entry.kind === 'form_submission') return entry.submission.submittedAt;
  return entry.changedAt;
}

type RightTab = 'polls' | 'forms' | 'messages';

interface RightTabButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
  count: number;
}

function RightTabButton({ label, active, onClick, count }: RightTabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'rounded px-2.5 py-1 text-xs font-medium font-mono transition-colors',
        'hover:bg-surface-3',
        active ? 'bg-surface-3 text-text shadow-sm' : 'text-text-muted',
      )}
    >
      {label}
      <span className="ml-1.5 tabular-nums text-text-muted">{count}</span>
    </button>
  );
}

export function FeedbackInbox(): JSX.Element {
  const {
    activeStudio,
    activeGame,
    buildMarkers,
    devStages,
    feedbackIsUnread: isUnread,
    feedbackMarkRead: markRead,
  } = useShell();
  // Single shared chip filter applies to all four feeds (activity,
  // messages, polls, form submissions). Lives at the top of the page
  // rather than inside any one feed card.
  const [activeChipId, setActiveChipId] = useState<string | null>(null);
  const [pollTab, setPollTab] = useState<'active' | 'closed'>('active');
  // Default tab on first visit: Messages (matches the previous
  // behaviour, where Messages was the heaviest pane below the Polls
  // card). The other tabs flip on activity-feed click.
  const [rightTab, setRightTab] = useState<RightTab>('messages');

  // Insights "View in feed" deep-link. The Insights page links here
  // with ?focus=<feedback_pool.id>; once the matching message arrives
  // in the loaded `groups`, we route the existing highlight/scroll
  // path (the same one activity-panel clicks use). The not-found
  // banner shows once we've waited for the initial fetch but the
  // requested id still isn't present - usually because it lives on a
  // later page of feedback.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  // Insights "View in feed" on a poll mention sends ?poll=<pollId>
  // instead of ?focus. The aspect's feedbackRecordId points at the
  // AI-synth feedback_pool row, which lives outside pollGroups - those
  // bundle the original Discord poll message rows, which are joined
  // back via feedback_poll.feedback_id. So we match the poll id
  // directly against g.head.pollStatus.pollId instead.
  const pollIdParam = searchParams.get('poll');
  const [focusNotFound, setFocusNotFound] = useState(false);
  // Tracks the last focus value we already routed, so we don't re-fire
  // the highlight on every render once the user scrolls or interacts.
  const resolvedFocusRef = useRef<string | null>(null);
  // Tracks auto-pagination attempts per focusId so the inbox can walk
  // back through pages until it finds the focused message (forum
  // threads are often older than the first page). Capped to avoid
  // runaway scrolling through huge inboxes.
  const focusPaginateRef = useRef<{ focusId: string | null; attempts: number }>({
    focusId: null,
    attempts: 0,
  });
  const FOCUS_AUTO_PAGINATE_MAX = 6;
  const clearFocus = useCallback((): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('focus');
        next.delete('poll');
        return next;
      },
      { replace: true },
    );
    setFocusNotFound(false);
  }, [setSearchParams]);

  if (activeGame === null) {
    // The route is nested under /s/.../g/.../, so this is a transient
    // state - covers the gap between provider mount and shell-data load.
    return (
      <Card>
        <Card.Content>
          <p className="text-text-muted text-sm">Loading game…</p>
        </Card.Content>
      </Card>
    );
  }

  const { entities, loading: entitiesLoading } = useWatchedEntities(activeGame.id);

  const sortedEntities = useMemo(() => sortEntitiesForChips(entities), [entities]);
  const entityById = useMemo(() => {
    const map = new Map<string, DiscordGuildChannel>();
    for (const e of sortedEntities) map.set(chipIdFor(e), e);
    return map;
  }, [sortedEntities]);
  const channelByChannelId = useMemo(() => {
    // Form submissions store the raw channel snowflake (no chip-id
    // shape) on `surveyChannelId`. Building this index once lets every
    // form-submission render path resolve a friendly `#name` without
    // recomputing.
    const map = new Map<string, DiscordGuildChannel>();
    for (const e of sortedEntities) map.set(e.channelId, e);
    return map;
  }, [sortedEntities]);
  const chips: ChannelChip[] = useMemo(
    () => sortedEntities.map((e) => ({ id: chipIdFor(e), label: chipLabelFor(e) })),
    [sortedEntities],
  );
  const activeEntity = activeChipId === null ? null : entityById.get(activeChipId) ?? null;
  const entityFilter = activeEntity === null ? null : entityFilterFor(activeEntity);
  const activeChannelSnowflake = activeEntity?.channelId ?? null;

  // The watched set, in the shape the data hook scopes the "All channels"
  // view by. Every watched entity contributes its snowflake; forums also
  // contribute their name (forum-thread rows match on the parent name).
  const watchedScope = useMemo(() => {
    const channelIds: string[] = [];
    const forumParentNames: string[] = [];
    for (const e of sortedEntities) {
      channelIds.push(e.channelId);
      if (e.type === 'forum') forumParentNames.push(e.name);
    }
    return { channelIds, forumParentNames };
  }, [sortedEntities]);

  const {
    groups,
    activityEntries,
    pollGroups,
    formSubmissions,
    formSubmissionsHasMore,
    formSubmissionsLoadingMore,
    loadMoreFormSubmissions,
    messages,
    messagesCount,
    pollsCount,
    formsCount,
    loading,
    error,
    hasMore,
    loadMore,
  } = useFeedbackInbox({
    gameId: activeGame.id,
    entityFilter,
    watchedScope,
    // Hold the feed in its loading state until the watched-entity set has
    // resolved, so it never fetches against the transiently-empty scope.
    // This is the single readiness signal the deep-link resolvers rely on
    // (they just wait for `loading`).
    scopeReady: !entitiesLoading,
    buildMarkers,
    devStages,
  });

  // Navigate-away marks every visible item as read. We track the
  // newest lastActivityAt across BOTH groups and pollGroups - not
  // just activityGroups - so standalone-rendered rows (e.g. replies
  // on polls, which are kind 'single' and so don't qualify for the
  // activity panel) still advance the read watermark. Without this,
  // the unread-flag picks up those posted_at values via realtime but
  // the markRead advance never matches, leaving the sidebar dot lit
  // after navigation. Form submissions advance the same watermark via
  // `submittedAt` so their unread dots clear on navigate-away too.
  const newestActivityRef = useRef<string | null>(null);
  let newestSeen: string | null = null;
  for (const g of groups) {
    if (newestSeen === null || g.lastActivityAt > newestSeen) {
      newestSeen = g.lastActivityAt;
    }
  }
  for (const g of pollGroups) {
    if (newestSeen === null || g.lastActivityAt > newestSeen) {
      newestSeen = g.lastActivityAt;
    }
  }
  for (const s of formSubmissions) {
    if (newestSeen === null || s.submittedAt > newestSeen) {
      newestSeen = s.submittedAt;
    }
  }
  // Build-change entries (game-wide) advance the watermark too, so their
  // unread dot clears on navigate-away like every other activity kind.
  for (const e of activityEntries) {
    if (e.kind !== 'build_marker') continue;
    if (newestSeen === null || e.changedAt > newestSeen) {
      newestSeen = e.changedAt;
    }
  }
  newestActivityRef.current = newestSeen;
  useEffect(() => {
    return () => {
      const newest = newestActivityRef.current;
      if (newest !== null) void markRead(newest);
    };
  }, [markRead]);

  // Tab counts come from the hook's exact server-side counts (scoped to the
  // active channel selection), so "All channels" and the per-channel chips
  // reconcile regardless of how much each paginated feed has loaded.
  const filteredActivityEntries = useMemo(() => {
    if (activeChannelSnowflake === null) return activityEntries;
    return activityEntries.filter((entry) => {
      if (entry.kind !== 'form_submission') return true;
      return entry.submission.surveyChannelId === activeChannelSnowflake;
    });
  }, [activityEntries, activeChannelSnowflake]);

  const byExternalId = useMemo(() => {
    const map = new Map<string, InboxMessage>();
    for (const m of messages) {
      if (m.externalId !== null) map.set(m.externalId, m);
    }
    return map;
  }, [messages]);

  // Build-change divider rows, shared across the three feeds. Game-wide,
  // so they ignore the channel chip - interleaveBuildMarkers positions
  // each one among whichever rows the feed currently shows.
  const buildChanges = useMemo(
    () => toBuildChanges(buildMarkers, devStages),
    [buildMarkers, devStages],
  );

  // Visible-count caps (10 + Show more) for each list. Reset on the
  // chip change that owns the list so a fresh filter starts at the top.
  const [feedVisible, setFeedVisible] = useState<number>(INITIAL_VISIBLE);
  const [activityVisible, setActivityVisible] = useState<number>(INITIAL_VISIBLE);
  useEffect(() => {
    setFeedVisible(INITIAL_VISIBLE);
    setActivityVisible(INITIAL_VISIBLE);
  }, [activeChipId]);

  // Local per-entry dismissal for the activity panel. Keyed by entry
  // (group/submission id), valued by the timestamp at click time. The
  // watermark on its own would mark every older entry as read on any
  // click, so we layer this map on top: a click only dismisses its own
  // dot. Storing the timestamp means a new reply on a previously
  // dismissed group (which advances lastActivityAt) re-lights the dot.
  // Resets on game change so dismissals don't bleed between games.
  const [dismissedActivityAt, setDismissedActivityAt] = useState<
    Map<string, string>
  >(() => new Map());
  useEffect(() => {
    setDismissedActivityAt(new Map());
  }, [activeGame.id]);

  const activityEntryKey = useCallback((entry: ActivityEntry): string => {
    if (entry.kind === 'group') return `group:${entry.group.id}`;
    if (entry.kind === 'form_submission') return `submission:${entry.submission.id}`;
    return `build:${entry.marker.id}`;
  }, []);

  const activityIsUnread = useCallback(
    (entry: ActivityEntry): boolean => {
      const ts = activityUnreadTimestamp(entry);
      if (!isUnread(ts)) return false;
      const dismissed = dismissedActivityAt.get(activityEntryKey(entry));
      if (dismissed !== undefined && ts <= dismissed) return false;
      return true;
    },
    [dismissedActivityAt, isUnread, activityEntryKey],
  );

  // Sidebar `Feedback` dot sync. The sidebar reads `feedbackHasUnread`
  // which is `latestActivityAt > lastReadAt` - i.e. it tracks the
  // watermark, not the local dismissal map. Once the user has clicked
  // through every unread entry in the panel, advance the watermark to
  // the newest entry so the sidebar dot clears too. We bound the
  // advance to filteredActivityEntries' newest timestamp so we don't
  // outrun activity the user hasn't seen yet (e.g. on the other
  // channel filter).
  useEffect(() => {
    if (filteredActivityEntries.length === 0) return;
    let newest: string | null = null;
    let anyStillUnread = false;
    for (const entry of filteredActivityEntries) {
      const ts = activityUnreadTimestamp(entry);
      if (newest === null || ts > newest) newest = ts;
      if (activityIsUnread(entry)) {
        anyStillUnread = true;
        break;
      }
    }
    if (anyStillUnread) return;
    if (newest !== null && isUnread(newest)) {
      void markRead(newest);
    }
  }, [filteredActivityEntries, activityIsUnread, isUnread, markRead]);

  // Activity-panel click flow. Three highlight states - one per pane -
  // so a click on a poll-vote activity scrolls the polls feed without
  // disturbing the message-feed view (and vice versa). Each highlight
  // clears itself after HIGHLIGHT_DURATION_MS; the matching feed
  // component handles the visual pulse.
  const [highlight, setHighlight] = useState<{
    groupId: string;
    rowId: string;
    nonce: number;
  } | null>(null);
  const [pollHighlight, setPollHighlight] = useState<{
    groupId: string;
    rowId: string;
    nonce: number;
  } | null>(null);
  const [formHighlight, setFormHighlight] = useState<{
    submissionId: string;
    nonce: number;
  } | null>(null);
  // Pulse on the messages-feed build divider when its activity entry is
  // clicked. Build dividers only register refs in the messages feed, so
  // this single highlight covers the only scrollable target.
  const [buildHighlight, setBuildHighlight] = useState<{
    markerId: string;
    nonce: number;
  } | null>(null);
  // highlight clear is started in the scroll-retry effect below, so the
  // 2.5s flash visibly begins WHEN the row arrives on screen rather than
  // when the highlight target is first set. Without this, a forum-post
  // card's flash would burn down while the card was still collapsed.
  useEffect(() => {
    if (pollHighlight === null) return;
    const t = setTimeout(() => setPollHighlight(null), HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(t);
  }, [pollHighlight]);
  useEffect(() => {
    if (formHighlight === null) return;
    const t = setTimeout(() => setFormHighlight(null), HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(t);
  }, [formHighlight]);
  useEffect(() => {
    if (buildHighlight === null) return;
    const t = setTimeout(() => setBuildHighlight(null), HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(t);
  }, [buildHighlight]);

  // One ref per inner row id - registered by FeedbackGroup /
  // ForumPostCard / PollGroupCard / FormSubmissionCard via the
  // registerRowRef callback. We keep three maps so a row id present in
  // multiple feeds doesn't collide.
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const pollRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const formRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const registerRowRef = useCallback(
    (rowId: string, el: HTMLDivElement | null): void => {
      if (el === null) {
        rowRefs.current.delete(rowId);
      } else {
        rowRefs.current.set(rowId, el);
      }
    },
    [],
  );
  const registerPollRowRef = useCallback(
    (rowId: string, el: HTMLDivElement | null): void => {
      if (el === null) {
        pollRowRefs.current.delete(rowId);
      } else {
        pollRowRefs.current.set(rowId, el);
      }
    },
    [],
  );
  const registerFormRowRef = useCallback(
    (rowId: string, el: HTMLDivElement | null): void => {
      if (el === null) {
        formRowRefs.current.delete(rowId);
      } else {
        formRowRefs.current.set(rowId, el);
      }
    },
    [],
  );
  const buildRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const registerBuildRowRef = useCallback(
    (markerId: string, el: HTMLDivElement | null): void => {
      if (el === null) {
        buildRowRefs.current.delete(markerId);
      } else {
        buildRowRefs.current.set(markerId, el);
      }
    },
    [],
  );

  const handleActivitySelect = useCallback(
    (entry: ActivityEntry): void => {
      // Dismiss this entry's dot locally. We deliberately don't
      // advance the inbox watermark here - that would clear every
      // older entry below this one too. The watermark still moves
      // forward on navigate-away (see the unmount effect above).
      const key = activityEntryKey(entry);
      const ts = activityUnreadTimestamp(entry);
      setDismissedActivityAt((prev) => {
        const next = new Map(prev);
        next.set(key, ts);
        return next;
      });
      if (entry.kind === 'form_submission') {
        // Switch to the Form submissions tab so the card is in the DOM
        // before the highlight effect tries to scroll it into view.
        setRightTab('forms');
        setFormHighlight({ submissionId: entry.submission.id, nonce: Date.now() });
        return;
      }
      if (entry.kind === 'build_marker') {
        // Build changes surface as divider rows in the Messages feed.
        // Switch to that tab, reveal every loaded group so the divider is
        // in the DOM, then pulse + scroll to it.
        setRightTab('messages');
        setFeedVisible((current) => Math.max(current, groups.length));
        setBuildHighlight({ markerId: entry.marker.id, nonce: Date.now() });
        return;
      }
      const matchedGroup = entry.group;
      // Route by source: a group whose head carries a poll lives in
      // the polls feed, so its activity entry should pulse the polls
      // panel rather than expand a row in the message feed. Tab
      // auto-switches if the poll is on the other tab so the row is
      // in the DOM before scroll.
      const status = matchedGroup.head.pollStatus;
      // Determine the activity-driving row id: head if the group's
      // lastActivity matches, otherwise the matching reply.
      let activityRowId = matchedGroup.head.id;
      if (matchedGroup.head.lastActivityAt !== matchedGroup.lastActivityAt) {
        for (const r of matchedGroup.replies) {
          if (r.lastActivityAt === matchedGroup.lastActivityAt) {
            activityRowId = r.id;
            break;
          }
        }
      }
      if (status !== null) {
        const now = Date.now();
        const closed =
          status.finalised ||
          (status.expiresAt !== null &&
            new Date(status.expiresAt).getTime() <= now);
        setRightTab('polls');
        setPollTab(closed ? 'closed' : 'active');
        setPollHighlight({
          groupId: matchedGroup.id,
          rowId: activityRowId,
          nonce: Date.now(),
        });
      } else {
        // Existing message-feed routing.
        setRightTab('messages');
        setFeedVisible((current) => {
          const idx = groups.findIndex((g) => g.id === matchedGroup.id);
          if (idx === -1 || idx < current) return current;
          return Math.max(current, groups.length);
        });
        setHighlight({
          groupId: matchedGroup.id,
          rowId: activityRowId,
          nonce: Date.now(),
        });
      }
    },
    [groups, activityEntryKey],
  );

  // Scroll into view after the row ref settles. Forum-post groups don't
  // mount their inner FeedbackRow (and thus don't register a row ref)
  // until forceOpenGroupId flips them to open - which is one more render
  // cycle past the highlight update. A single requestAnimationFrame
  // would fire before the inner row mounts, so we retry for ~400ms (~24
  // frames at 60fps) before giving up. Polls + flat reply chains land on
  // the first poll.
  useEffect(() => {
    if (highlight === null) return;
    let cancelled = false;
    let clearTimer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = performance.now();
    const SCROLL_LOOKUP_TIMEOUT_MS = 400;
    const tryScroll = (): void => {
      if (cancelled) return;
      const el = rowRefs.current.get(highlight.rowId);
      if (el !== undefined) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Start the flash-clear timer here so the pulse animation
        // begins from arrival, not from when the highlight target was
        // set (which may have been several pagination + render cycles
        // earlier for forum-post deep-links).
        clearTimer = setTimeout(() => setHighlight(null), HIGHLIGHT_DURATION_MS);
        return;
      }
      if (performance.now() - startedAt > SCROLL_LOOKUP_TIMEOUT_MS) {
        console.warn('[feedback-inbox] scroll target never registered', {
          rowId: highlight.rowId,
          groupId: highlight.groupId,
          waitedMs: Math.round(performance.now() - startedAt),
        });
        // Clear anyway so a missing row doesn't leave a stuck highlight.
        setHighlight(null);
        return;
      }
      requestAnimationFrame(tryScroll);
    };
    requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
      if (clearTimer !== null) clearTimeout(clearTimer);
    };
  }, [highlight]);
  useEffect(() => {
    if (pollHighlight === null) return;
    const id = requestAnimationFrame(() => {
      // PollGroupCard registers under the head row id (its rowId
      // matches the group's head bundle id); the activity-panel rowId
      // for a single-kind group is the same head id, so a single
      // lookup covers both keys.
      const el =
        pollRowRefs.current.get(pollHighlight.rowId) ??
        pollRowRefs.current.get(pollHighlight.groupId);
      if (el === undefined) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => cancelAnimationFrame(id);
  }, [pollHighlight]);
  useEffect(() => {
    if (formHighlight === null) return;
    const id = requestAnimationFrame(() => {
      const el = formRowRefs.current.get(formHighlight.submissionId);
      if (el === undefined) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => cancelAnimationFrame(id);
  }, [formHighlight]);
  useEffect(() => {
    if (buildHighlight === null) return;
    const id = requestAnimationFrame(() => {
      // The divider only renders when its build sits within the loaded
      // (visible) range; if it's off-page the lookup misses and we just
      // skip the scroll, same as the other feeds' best-effort targets.
      const el = buildRowRefs.current.get(buildHighlight.markerId);
      if (el === undefined) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => cancelAnimationFrame(id);
  }, [buildHighlight]);

  // Resolve ?focus=<feedback_pool.id> into the existing highlight/scroll
  // pipeline. Runs once per distinct focus value once `groups` is
  // populated: walks every loaded bundle (and the parallel
  // form-submission list), picks the matching row, and routes via the
  // appropriate tab. If both feeds have come back from the initial
  // fetch but the id is still missing, we surface a banner so the user
  // knows the message lives on a page we haven't loaded yet rather
  // than silently doing nothing.
  useEffect(() => {
    if (focusId === null) {
      resolvedFocusRef.current = null;
      focusPaginateRef.current = { focusId: null, attempts: 0 };
      setFocusNotFound(false);
      return;
    }
    if (resolvedFocusRef.current === focusId) return;
    // `loading` stays true until the watched scope resolves (see the
    // scopeReady gate in useFeedbackInbox), so this one guard covers both
    // an in-flight fetch and a not-yet-resolved scope - the feed is never
    // observed as a settled-but-empty page mid-resolution.
    if (loading) return;
    if (focusPaginateRef.current.focusId !== focusId) {
      focusPaginateRef.current = { focusId, attempts: 0 };
    }
    for (const g of groups) {
      const allRows = [g.head, ...g.replies];
      for (const row of allRows) {
        const match = row.messages.find((m) => m.id === focusId);
        if (match !== undefined) {
          resolvedFocusRef.current = focusId;
          setFocusNotFound(false);
          setRightTab('messages');
          setFeedVisible((current) => {
            const idx = groups.findIndex((gg) => gg.id === g.id);
            if (idx === -1 || idx < current) return current;
            return Math.max(current, groups.length);
          });
          setHighlight({ groupId: g.id, rowId: row.id, nonce: Date.now() });
          return;
        }
      }
    }
    // Form-submission targets carry the sibling feedback_pool.id as
    // the focus value; the FormSubmissionEntry stores it as
    // `feedbackPoolId`. Switch to the Forms tab so the card is in the
    // DOM before the scroll effect tries to find its ref.
    const formMatch = formSubmissions.find((s) => s.feedbackPoolId === focusId);
    if (formMatch !== undefined) {
      resolvedFocusRef.current = focusId;
      setFocusNotFound(false);
      setRightTab('forms');
      setFormHighlight({ submissionId: formMatch.id, nonce: Date.now() });
      return;
    }
    // Not in the loaded page. If either feed has more to fetch and we
    // haven't hit the cap, pull another page - the effect re-fires
    // when groups or formSubmissions grows. Forum-post mentions and
    // older form submissions are often past the first page so this is
    // the common case for deep-links.
    if (focusPaginateRef.current.attempts < FOCUS_AUTO_PAGINATE_MAX) {
      let paginated = false;
      if (hasMore) {
        void loadMore();
        paginated = true;
      }
      if (formSubmissionsHasMore) {
        void loadMoreFormSubmissions();
        paginated = true;
      }
      if (paginated) {
        focusPaginateRef.current.attempts += 1;
        return;
      }
    }
    // Couldn't match and there's nothing more to page in. Crucially we do
    // NOT latch resolvedFocusRef here. The watched-channel scope resolves
    // asynchronously (connection -> channels -> watchers), so the feed can
    // be transiently empty on the first settle - the watched entities land
    // a beat after the connection does. Latching on that empty window would
    // give up permanently and never retry once the scoped page arrives,
    // which is exactly why the deep-link stopped highlighting. Leaving it
    // unlatched lets this effect re-run as `groups` / `formSubmissions`
    // grow and resolve the target then (the success branch above latches).
    //
    // Only surface the "not in view" banner once the feed has actually
    // loaded content that simply doesn't contain the target. While both
    // feeds are still empty we stay quiet rather than flash a misleading
    // banner over a feed that's about to populate.
    if (groups.length > 0 || formSubmissions.length > 0) {
      setFocusNotFound(true);
      console.warn('[feedback-inbox] focus not found in loaded feed', {
        focusId,
        loadedGroups: groups.length,
        loadedFormSubmissions: formSubmissions.length,
        attempts: focusPaginateRef.current.attempts,
      });
    }
  }, [
    focusId,
    groups,
    formSubmissions,
    loading,
    hasMore,
    loadMore,
    formSubmissionsHasMore,
    loadMoreFormSubmissions,
  ]);

  // Resolve ?poll=<pollId>: walk pollGroups looking for the poll whose
  // head row carries the matching pollStatus.pollId, switch to the
  // Polls tab + the right Active/Closed sub-tab, and pulse the row.
  // Mirrors handleActivitySelect's poll-routing branch so a deep-link
  // and a click-in-the-activity-panel land at the same place. Uses a
  // separate resolved ref keyed off pollIdParam so a back-and-forth
  // between ?focus and ?poll URLs doesn't re-fire stale highlights.
  const resolvedPollRef = useRef<string | null>(null);
  useEffect(() => {
    if (pollIdParam === null) {
      resolvedPollRef.current = null;
      return;
    }
    if (resolvedPollRef.current === pollIdParam) return;
    // Same single readiness guard as the ?focus resolver: `loading` stays
    // true until the watched scope resolves, so pollGroups are settled by
    // the time we walk them.
    if (loading) return;
    for (const g of pollGroups) {
      if (g.head.pollStatus?.pollId !== pollIdParam) continue;
      resolvedPollRef.current = pollIdParam;
      setRightTab('polls');
      setPollTab(isClosed(g, Date.now()) ? 'closed' : 'active');
      setPollHighlight({ groupId: g.id, rowId: g.head.id, nonce: Date.now() });
      return;
    }
  }, [pollIdParam, pollGroups, loading]);

  if (error !== null) {
    return (
      <Alert tone="danger">
        Couldn't load feedback. {error}
      </Alert>
    );
  }

  // First load: until the watched-entity set has resolved AND the
  // initial feed pull has come back, show a single loading card. Once
  // there are watched chips OR rows to render, the inbox shell takes
  // over and the chip header stays mounted across filter changes.
  if (chips.length === 0 && groups.length === 0 && formSubmissions.length === 0) {
    if (loading || entitiesLoading) {
      return (
        <Card>
          <Card.Content>
            <p className="text-text-muted text-sm">Loading feedback…</p>
          </Card.Content>
        </Card>
      );
    }
    return (
      <FeedbackEmptyState studioShortId={activeStudio.shortId} gameShortId={activeGame.shortId} />
    );
  }

  // The feed shows up to feedVisible groups locally; once the local
  // cap matches what's already fetched and the network has more, the
  // button switches to "Load more" and pulls another page.
  const visibleGroups = groups.slice(0, feedVisible);
  const localFeedHasMore = feedVisible < groups.length;
  const feedShowMore = localFeedHasMore || hasMore;
  const feedShowMoreLabel = loading ? 'Loading…' : 'Show more';
  const onFeedShowMore = (): void => {
    if (localFeedHasMore) {
      setFeedVisible((c) => Math.min(groups.length, c + VISIBLE_STEP));
      return;
    }
    void loadMore();
  };


  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      {/* Page-level channel filter. Applies to all four feeds
          (activity, messages, polls, form submissions). Sits visually
          separated from the feed cards so the scope is read at-a-glance
          before any feed content. */}
      {chips.length > 1 && (
        <div className="lg:shrink-0">
          <FeedbackChannelFilter
            channels={chips}
            active={activeChipId}
            onChange={setActiveChipId}
          />
        </div>
      )}
      {/* Activity column on the left takes 1/3; the right column is a
          single tabbed card holding Polls, Form submissions and Messages.
          The right-column feed cards carry a fixed height (h-[42rem]) so
          the stream stays a predictable size rather than collapsing to
          fit the viewport on short laptop screens. The activity panel
          stretches to the full row height (lg:h-full) so it lines up with
          the bottom of the feed card, tab strip included. */}
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
        <aside className="w-full lg:w-1/3 lg:flex-shrink-0">
          <ActivityPanel
            entries={filteredActivityEntries}
            byExternalId={byExternalId}
            channelById={channelByChannelId}
            onSelect={handleActivitySelect}
            visibleCount={activityVisible}
            onShowMore={() =>
              setActivityVisible((c) =>
                Math.min(filteredActivityEntries.length, c + VISIBLE_STEP),
              )
            }
            isUnread={activityIsUnread}
            maxHeightClass="h-[42rem] lg:h-full"
          />
        </aside>
        {/* Right column: tab strip at the top, then a single visible
            pane below. Each pane keeps its own Card chrome (header,
            tabs/counts inside the pane), so PollsFeed's existing
            Active/Closed sub-tabs and the messages feed's row treatment
            survive intact. The tab strip itself is a thin chip row -
            the inner cards take over from there. */}
        <div className="flex min-w-0 flex-1 flex-col gap-2 lg:gap-3">
          <div
            role="tablist"
            aria-label="Inbox panes"
            className="flex gap-1 self-start rounded-md border border-border bg-surface p-0.5"
          >
            <RightTabButton
              label="Polls"
              active={rightTab === 'polls'}
              onClick={() => setRightTab('polls')}
              count={pollsCount}
            />
            <RightTabButton
              label="Form submissions"
              active={rightTab === 'forms'}
              onClick={() => setRightTab('forms')}
              count={formsCount}
            />
            <RightTabButton
              label="Messages"
              active={rightTab === 'messages'}
              onClick={() => setRightTab('messages')}
              count={messagesCount}
            />
          </div>

          {rightTab === 'polls' && (
            <PollsFeed
              groups={pollGroups}
              byExternalId={byExternalId}
              entityFilter={entityFilter}
              buildChanges={buildChanges}
              activeTab={pollTab}
              onTabChange={setPollTab}
              highlightRowId={pollHighlight?.rowId ?? null}
              forceOpenGroupId={pollHighlight?.groupId ?? null}
              registerRowRef={registerPollRowRef}
              heightClass="h-[42rem]"
            />
          )}

          {rightTab === 'forms' && (
            <Card className="flex h-[42rem] flex-col bg-surface">
              <Card.Header className="rounded-t-md border-b border-border bg-surface pb-4">
                <h2 className="text-sm font-medium text-text">Form submissions</h2>
              </Card.Header>
              <Card.Content className="no-scrollbar flex-1 overflow-y-auto p-0">
                <FormSubmissionsFeed
                  entries={formSubmissions}
                  channelById={channelByChannelId}
                  activeChannelId={activeChannelSnowflake}
                  buildChanges={buildChanges}
                  highlightSubmissionId={formHighlight?.submissionId ?? null}
                  registerRowRef={registerFormRowRef}
                  hasMoreFromServer={formSubmissionsHasMore}
                  loadingMore={formSubmissionsLoadingMore}
                  onLoadMore={() => void loadMoreFormSubmissions()}
                />
              </Card.Content>
            </Card>
          )}

          {rightTab === 'messages' && (
            <Card className="flex h-[42rem] flex-col bg-surface">
              <Card.Header className="rounded-t-md border-b border-border bg-surface pb-4">
                <h2 className="text-sm font-medium text-text">Message feed</h2>
              </Card.Header>
              {focusNotFound && (
                <div className="border-b border-border bg-surface px-4 py-2 text-xs text-text-muted">
                  The message you came from isn&apos;t in the current view.{' '}
                  <button
                    type="button"
                    onClick={clearFocus}
                    className="text-link underline"
                  >
                    Clear
                  </button>
                </div>
              )}
              <Card.Content className="no-scrollbar flex-1 overflow-y-auto p-0">
                {/* Keyed on the active chip so a filter switch remounts
                    the body, triggering a quick fade-in. */}
                <div
                  key={activeChipId ?? 'all'}
                  className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150"
                >
                  {loading && groups.length === 0 ? (
                    <p className="px-4 py-6 text-text-muted text-sm">Loading…</p>
                  ) : groups.length === 0 ? (
                    <p className="px-4 py-6 text-text-muted text-sm">
                      No feedback in this channel yet.
                    </p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {interleaveBuildMarkers(
                        visibleGroups,
                        buildChanges,
                        (g) => g.chronologicalAt,
                      ).map((row) =>
                        row.kind === 'build' ? (
                          <li key={`build:${row.change.marker.id}`}>
                            <BuildChangedRow
                              marker={row.change.marker}
                              highlighted={buildHighlight?.markerId === row.change.marker.id}
                              innerRef={(el) =>
                                registerBuildRowRef(row.change.marker.id, el)
                              }
                            />
                          </li>
                        ) : (
                          <li key={row.item.id}>
                            <FeedbackGroup
                              group={row.item}
                              byExternalId={byExternalId}
                              forceOpenGroupId={highlight?.groupId ?? null}
                              highlightRowId={
                                highlight?.groupId === row.item.id ? highlight.rowId : null
                              }
                              registerRowRef={registerRowRef}
                            />
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                  {feedShowMore && (
                    <div className="flex justify-center border-t border-border px-4 py-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onFeedShowMore}
                        disabled={loading}
                      >
                        {feedShowMoreLabel}
                      </Button>
                    </div>
                  )}
                </div>
              </Card.Content>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
